import { inject, signal, computed } from '@angular/core';
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TaskDialogData, TaskDialogMode, TaskDialogResult, TaskDialogResultAction } from './task.dialog.model';
import { ProgramsStore } from '../../../services/stores/programs.store';
import { AppDropdownOption } from '../../../shared/components/app-dropdown/app-dropdown.component';
import { WORLD_CLOCK_DATA } from '../../world-clock/world-clock.data';

export abstract class TaskFormDialogBase<TLog, TValue> {
  protected readonly dialogRef = inject<DialogRef<TaskDialogResult<TLog, TValue>>>(DialogRef);

  protected readonly data = inject<TaskDialogData>(DIALOG_DATA);

  protected readonly fb = inject(FormBuilder);

  protected readonly programsStore = inject(ProgramsStore);
  readonly programOptions = computed<AppDropdownOption[]>(() =>
    this.programsStore.programs().map((p) => ({
      value: p.id,
      label: p.displayLabel,
    }))
  );

  readonly timezones = signal<string[]>(['UTC', 'Europe/Madrid', 'Europe/Bucharest', 'America/New_York', 'America/Los_Angeles']);

  readonly timezoneOptions = computed<AppDropdownOption[]>(() =>
    this.timezones().map((value, index) => ({
      value: value,
      label: value,
    }))
  );

  readonly timezoneWorldClockOptions = computed<AppDropdownOption[]>(() =>
    WORLD_CLOCK_DATA.map((item) => ({
      value: item.ianaTimeZone,
      label: item.ianaTimeZone,
    }))
  );

  readonly log = signal<TLog | undefined>(this.data.log as TLog | undefined);
  readonly mode = signal<TaskDialogData['mode']>(this.data.mode);
  readonly title = signal<string>(this.data.title);

  readonly isViewOnly = computed(() => this.mode() === TaskDialogMode.VIEW);
  readonly isEdit = computed(() => this.mode() === TaskDialogMode.EDIT);
  readonly isCreate = computed(() => this.mode() === TaskDialogMode.CREATE);

  protected abstract readonly form: FormGroup;

  protected close(result: TaskDialogResult<TLog, TValue>): void {
    this.dialogRef.close(result);
  }

  protected cancel(): void {
    this.dialogRef.close({ action: TaskDialogResultAction.CANCEL });
  }

  protected getResultValue(): TValue {
    return this.form.getRawValue() as TValue;
  }

  protected requireLog(): TLog {
    const l = this.log();
    if (!l) {
      throw new Error('TaskFormDialogBase: log is required in UPDATE mode');
    }
    return l;
  }

  submit(): void {
    if (this.isViewOnly()) return;

    this.form.markAllAsTouched();
    if (!this.form.valid) return;

    const value = this.getResultValue();

    this.close(
      this.isCreate()
        ? {
            action: TaskDialogResultAction.CREATE,
            resultTaskFormValue: value,
          }
        : {
            action: TaskDialogResultAction.UPDATE,
            inputLog: this.requireLog(),
            resultTaskFormValue: value,
          }
    );
  }
}
